import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { StyledItemLabel, StyledPaperContainer, StyledSection } from './StyledComponents';
import { RowItemMemo } from './rowItem/RowItemMemo';
import { Skeleton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-v7';

const INITIAL_BATCH_SIZE = 20;
const BATCH_INCREMENT = 10;
const FULL_RENDER_THRESHOLD = 10;

interface ItemListGroupProps {
	label: string;
	items: ILockerGateItem[];
	isLoading: boolean;
}

export const ItemListGroup = ({ label, items, isLoading }: ItemListGroupProps) => {
	const itemCount = items.length;
	const atLeastOneItem = itemCount > 0;
	const noData = !isLoading && !atLeastOneItem;
	const { hash } = useLocation();
	const smallGroup = itemCount <= FULL_RENDER_THRESHOLD;

	const itemIndexMap = useMemo(() => {
		return items.reduce<Map<string, number>>((acc, item, index) => {
			acc.set(item.id, index);
			return acc;
		}, new Map());
	}, [items]);

	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const [visibleCount, setVisibleCount] = useState(() =>
		smallGroup ? itemCount : Math.min(itemCount, INITIAL_BATCH_SIZE)
	);

	useEffect(() => {
		setVisibleCount(smallGroup ? itemCount : Math.min(itemCount, INITIAL_BATCH_SIZE));
	}, [itemCount, smallGroup]);

	const loadMore = useCallback(() => {
		if (smallGroup) return;
		setVisibleCount((prev) => {
			if (prev >= itemCount) return prev;
			return Math.min(prev + BATCH_INCREMENT, itemCount);
		});
	}, [itemCount, smallGroup]);

	const hasMore = !smallGroup && visibleCount < itemCount;
	const displayedItems = useMemo(
		() => (smallGroup ? items : items.slice(0, visibleCount)),
		[items, visibleCount, smallGroup]
	);

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel || !hasMore) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					loadMore();
				}
			},
			{ root: null, rootMargin: '200px' }
		);

		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasMore, loadMore]);

	useEffect(() => {
		if (!hash) return;
		const targetId = hash.replace('#', '');
		const targetIndex = itemIndexMap.get(targetId);
		if (targetIndex === undefined) return;
		setVisibleCount((prev) => Math.max(prev, targetIndex + 1));
	}, [hash, itemIndexMap]);

	if (noData) return <></>;

	return (
		<StyledPaperContainer>
			<StyledItemLabel>{label}</StyledItemLabel>
			{isLoading && (
				<Skeleton
					variant='rectangular'
					width={'100%'}
					height={250}
					animation='wave'
					sx={{ bgcolor: 'white' }}
				/>
			)}
			{!isLoading && (
				<StyledSection>
					{displayedItems.map((item) => (
						<RowItemMemo key={item.id} {...item} />
					))}
					{hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
				</StyledSection>
			)}
		</StyledPaperContainer>
	);
};
